import {
  LayoutDashboard,
  LogOut,
  MessageSquareQuote,
  Package,
  Settings,
  ShoppingBag,
  Star,
  UserCog,
} from 'lucide-react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { routes } from '@/app/router/route-paths';
import { useAdminAuth } from '@/features/admin';
import { ThemeToggle } from '@/shared/components/ThemeToggle';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/utils/cn';

const navigation = [
  { to: routes.admin.dashboard, label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: routes.admin.products, label: 'Products', icon: Package, end: false },
  { to: routes.admin.orders, label: 'Orders', icon: ShoppingBag, end: false },
  { to: routes.admin.reviews, label: 'Reviews', icon: Star, end: false },
  { to: routes.admin.testimonials, label: 'Testimonials', icon: MessageSquareQuote, end: false },
  { to: routes.admin.settings, label: 'Settings', icon: Settings, end: false },
  { to: routes.admin.account, label: 'Account', icon: UserCog, end: false },
];

function NavItems({ onNavigate }) {
  return navigation.map(({ to, label, icon: Icon, end }) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition',
          isActive
            ? 'bg-brand-500/10 text-brand-700 dark:text-brand-100'
            : 'text-[var(--app-muted)] hover:bg-black/5 hover:text-[var(--app-fg)] dark:hover:bg-white/5',
        )
      }
    >
      <Icon className="size-4" aria-hidden="true" />
      {label}
    </NavLink>
  ));
}

export function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate(routes.admin.login, { replace: true });
  }

  return (
    <div className="min-h-screen bg-[var(--app-bg)] text-[var(--app-fg)]">
      <header className="sticky top-0 z-30 border-b bg-[color-mix(in_oklab,var(--app-surface)_92%,transparent)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <span className="font-serif text-lg font-semibold tracking-wide">Ayat</span>
            <span className="rounded-full bg-brand-500/10 px-2 py-0.5 text-xs font-semibold text-brand-700 dark:text-brand-100">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-[var(--app-muted)] sm:inline">
              {admin?.name ?? admin?.email}
            </span>
            <ThemeToggle />
            <Button variant="secondary" onClick={handleLogout} className="gap-2">
              <LogOut className="size-4" aria-hidden="true" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
        {/* Mobile nav */}
        <nav
          aria-label="Admin navigation"
          className="flex gap-1 overflow-x-auto border-t px-2 py-2 md:hidden"
        >
          <NavItems />
        </nav>
      </header>

      <div className="mx-auto flex max-w-7xl gap-8 px-4 py-8 sm:px-6 lg:px-8">
        <aside className="hidden w-56 shrink-0 md:block">
          <nav aria-label="Admin navigation" className="sticky top-24 grid gap-1">
            <NavItems />
          </nav>
        </aside>
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
